import React, { useEffect, useState } from 'react';
import { FormDetailModel } from 'types/FormTypes';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from 'components/atomic/LoadingIndicator';
import FormImage from './FormImage';
import CustomSelectInput from 'components/atomic/CustomSelectInput';

type OptionType = {
  value: string;
  label: string;
};

type FormProductPurchasedProps = {
  formData: FormDetailModel;
  formRef: FormikProps<any> | null;
  name: string;
  updateFormField: (name: string, value: any) => void;
};

const FormProductPurchased: React.FC<FormProductPurchasedProps> = ({
  name,
  formRef,
  formData,
  updateFormField,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'drawers.formDrawer.textArea',
  });

  // The admin picks Shopify products in the form builder; the selected list is
  // stored on config.products as { value: shopifyProductId, label: productName }.
  const { products: rawProducts, sortingMethod } =
    (formData?.config as any) || {};

  const products: OptionType[] = React.useMemo(() => {
    const list = Array.isArray(rawProducts) ? [...rawProducts] : [];
    if (sortingMethod === 'alphabetical') {
      list.sort((a, b) => (a?.label || '').localeCompare(b?.label || ''));
    } else if (sortingMethod === 'random') {
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
      }
    }
    return list;
  }, [rawProducts, sortingMethod]);

  const [selectedProduct, setSelectedProduct] = useState<OptionType>({
    value: '',
    label: '',
  });

  useEffect(() => {
    if (formRef && formRef.values[name]) {
      const { value, label } = formRef.values[name];
      setSelectedProduct({ value, label });
    }

    formRef?.validateForm();
  }, []);

  const updateProduct = (value: OptionType) => {
    setSelectedProduct(value);
    // Persist both the Shopify product id (value) and product name (label).
    updateFormField(name, {
      value: value?.value,
      label: value?.label,
    });
  };

  return formRef ? (
    <div className='w-full h-full flex flex-col justify-start px-3 overflow-auto'>
      {formData.image && (
        <FormImage image={formData.image} alt='form-question-image' />
      )}
      <h5
        tabIndex={0}
        className={`text-left text-lg leading-formTitle font-semibold text-black ${
          !formData.subText ? 'mb-3' : 'mb-0'
        }`}
      >
        {formData.text}
      </h5>
      {formData.subText && (
        <p
          tabIndex={0}
          className='mt-1.5 mb-3 text-left text-xs font-semibold leading-formSubtitle text-muted'
        >
          {formData.subText}
        </p>
      )}
      <div className='relative w-full flex'>
        <CustomSelectInput
          required
          type='text'
          key='product-purchased'
          name='product-purchased'
          placeholder={t('placeholder')}
          options={products}
          selected={selectedProduct}
          onChange={(option: OptionType) => updateProduct(option)}
        />
      </div>
    </div>
  ) : (
    <LoadingIndicator />
  );
};

export default FormProductPurchased;
